import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { map, Observable, Subject } from 'rxjs';

import {
  User,
  ContactRequest,
  InvitedUserCreation,
  InvitedUser,
  FullUser,
} from '@pulpo/pulpo-models';
import { APP_CONFIG } from '@pulpo/app-config';
import ApiService from './api.service.abstract';

@Injectable({ providedIn: 'root' })
export class UserService extends ApiService<User> {
  usersChangedSubject: Subject<User[]> = new Subject<User[]>();

  constructor(@Inject(APP_CONFIG) environment: any, private http: HttpClient) {
    super(environment, http, 'user/api/users');
    if (environment.name === 'localDev') {
      this.url = 'http://localhost:8095';
    }
  }

  public queryFullUserPage(
    req?: any
  ): Observable<HttpResponse<{ content: FullUser[]; totalElements: number }>> {
    const options = this.createRequestOption(req);
    return this.http
      .get<any>(`${this.url}/${this.endpoint}/page`, {
        params: options,
        observe: 'response',
      })
      .pipe(
        map((response) =>
          response.body?.page
            ? new HttpResponse({
                ...response,
                url: response.url || undefined,
                body: {
                  content: response.body.content,
                  totalElements: response.body.page.totalElements,
                },
              })
            : response
        )
      );
  }

  /** Narrower than the generic update: PUT .../{id}/activation, gated isTechnicalAdmin() or isProducer(). */
  setActivation(id: string, activated: boolean): Observable<HttpResponse<User>> {
    return this.http.put<User>(
      `${this.url}/${this.endpoint}/${id}/activation`,
      { activated },
      { observe: 'response' }
    );
  }

  listByGroupId(groupId: string): Observable<HttpResponse<User[]>> {
    return this.http.get<User[]>(`${this.url}/${this.endpoint}`, {
      params: { groupId },
      observe: 'response',
    });
  }

  findTempById(id: number): Observable<HttpResponse<User>> {
    return this.http.get<User>(`${this.url}/${this.endpoint}/temp/${id}`, {
      observe: 'response',
    });
  }

  activate(key: string) {
    return this.http.post(`${this.url}/user/public/users/activate/${key}`, {
      observe: 'response',
    });
  }

  sendContactRequest(request: ContactRequest) {
    return this.http.post<ContactRequest>(
      `${this.url}/public/users` + '/contact',
      request,
      { observe: 'response' }
    );
  }

  resetPasswordInit(mail: string) {
    return this.http.post<User>(
      `${this.url}/user/public/users` + '/reset-password/init',
      {
        login: mail,
        sourceName: 'pulpo',
        resetBaseUrl: window.location.origin + '#/login/reset',
      },
      { observe: 'response' }
    );
  }

  resetPasswordFinish(keyAndPassword: { key: string; newPassword: string }) {
    return this.http.post<User>(
      `${this.url}/user/public/users` + '/reset-password/finish',
      keyAndPassword,
      { observe: 'response' }
    );
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.http.post<User>(
      `${this.url}/${this.endpoint}` + '/change-password',
      {
        currentPassword,
        newPassword,
      }
    );
  }

  enroll(mail: number): Observable<HttpResponse<User>> {
    return this.http.get<User>(
      `${this.url}/${this.endpoint}/enroll?mail=${mail}`,
      {
        observe: 'response',
      }
    );
  }

  unroll(userId: number): Observable<HttpResponse<User>> {
    return this.http.get<User>(
      `${this.url}/${this.endpoint}/unroll?userId=${userId}`,
      {
        observe: 'response',
      }
    );
  }

  invite(mail: string, groupId: string): Observable<HttpResponse<void>> {
    return this.http.post<void>(
      `${this.url}/user/api/user-groups/${groupId}/invite`,
      {
        email: mail,
        baseUrl: null,
      },
      {
        observe: 'response',
      }
    );
  }

  getInvitedUser(key: string): Observable<HttpResponse<InvitedUser>> {
    return this.http.get<InvitedUser>(
      `${this.url}/user/public/invited-users/${key}`,
      {
        observe: 'response',
      }
    );
  }

  validateInvitedUser(request: InvitedUserCreation) {
    return this.http.post<User>(
      `${this.url}/user/public/users/`,
      { ...request },
      {
        observe: 'response',
      }
    );
  }
}
